import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
@Component({
  selector: 'lib-paginator',
  imports: [ChevronDownIconComponent, TranslocoPipe],
  templateUrl: './paginator.component.html',
  styleUrl: './paginator.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PaginatorComponent {
  currentPage = input.required<number>();
  totalPages = input.required<number>();
  nextBtnLabel = input<string>('Next');
  previousBtnLabel = input<string>('Previous');
  nextBtnIcon = input<string>('');
  previousBtnIcon = input<string>('');
  goTo = output<number>();
  next = output<number>();
  previous = output<number>();
  pages: number[] = [];
  pageBtnStyle = input<string>();
  showChangeSize = input<boolean>(false);
  currentSize = model<number>(0);
  sizeOptions = input<number[]>([]);
  handleChangeSize = output<number>();
  sizeOptionsLabel = input<string>('Rows No.');
  /** Total items across all pages; with UI_Version_2 the paginator shows "Showing X of Y". */
  totalItems = input<number | null | undefined>(null);
  /** Rows on this page; only needed when the page size (currentSize) isn't passed. */
  pageItemCount = input<number | null | undefined>(null);

  private readonly uiVersion2Enabled = !!inject(FeatureFlagsService, {
    optional: true,
  })?.getFeatureFlagValue(FEATURE_FLAGS.SYSTEM.ui_version_2);
  protected readonly showing = computed(() => {
    const total = this.totalItems();
    if (!this.uiVersion2Enabled || total == null || total <= 0) {
      return null;
    }
    const size = this.currentSize();
    const onPage =
      this.pageItemCount() ??
      (size > 0
        ? Math.max(0, Math.min(size, total - (this.currentPage() - 1) * size))
        : null);
    return onPage == null ? null : { count: onPage, total };
  });
  isRtl = signal<boolean>(false);

  constructor() {
    effect(() => {
      this.pages = this.getPages(this.currentPage(), this.totalPages());
    });

    this.isRtl.set(document.dir === 'rtl');
  }

  getPreviousIcon(): string {
    return this.isRtl() ? this.nextBtnIcon() : this.previousBtnIcon();
  }

  getNextIcon(): string {
    return this.isRtl() ? this.previousBtnIcon() : this.nextBtnIcon();
  }

  onGoTo(page: number): void {
    this.goTo.emit(page);
  }

  onNext(): void {
    this.next.emit(this.currentPage() + 1);
  }

  onPrevious(): void {
    this.previous.emit(this.currentPage() - 1);
  }

  getPages(current: number, total: number): number[] {
    if (total <= 7) {
      return [...Array(total).keys()].map(x => ++x);
    }

    if (current > 5) {
      if (current >= total - 4) {
        return [1, -1, total - 4, total - 3, total - 2, total - 1, total];
      } else {
        return [1, -1, current - 1, current, current + 1, -1, total];
      }
    }

    return [1, 2, 3, 4, 5, -1, total];
  }

  changeSize(size: number) {
    this.currentSize.set(size);
    this.handleChangeSize.emit(size);
  }
}
